import { useState, useEffect, useRef } from "react";
import {
    SortableTreeWithoutDndContext as SortableTree,
    insertNode,
    find,
    walk,
    changeNodeAtPath,
    removeNodeAtPath,
} from "@nosferatu500/react-sortable-tree";
import "@nosferatu500/react-sortable-tree/style.css"; // This only needs to be imported once in your app
import { TextField, IconButton, Tooltip, Box, Stack } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import AddNodeDialog from "./AddNodeDialog";
import ConfirmDialog from "../../Popup/ConfirmDialog";

import ExternalNode, { externalNodeType } from "./ExternalNode";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import TabbedPage from "../../../../TabbedPage";
import MoreMenu from "../../utlis/MoreMenu";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { AdminTable } from "../../utlis";
import { useTranslation } from 'react-i18next';
import { useDispatch } from "react-redux";
import { showAlert } from "src/redux/slices/AlertSlice";
import axios from "axios";

const NavigationStep = ({
    currentNav,
    navAll,
    addContext,
    setNavAll,
    markNavChanged,
    removeNode,
    newNav,
    navElements,
}) => {
    const [currentRowInfo, setCurrentRowInfo] = useState(null);
    const [showAddNodeDialog, setShowAddNodeDialog] = useState(false);
    const [showEditIconDialog, setShowEditIconDialog] = useState(false);
    const [editRowInfo, setEditRowInfo] = useState(null);
    const [deleteRowInfo, setDeleteRowInfo] = useState(null);
    const [scrollSearchNodeId, setScrollSearchNodeId] = useState(null);
    const [rows, setRows] = useState([]);
    const [selected, setSelected] = useState([]);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const tempIdRef = useRef(-1);
    const autoPlacementAppliedRef = useRef(false);
    const pendingScrollTargetIdRef = useRef(null);
    const scrollSearchResetTimerRef = useRef(null);
    const typeLabels = {
        content_list: t("AddNodeDialog.NavTypes.content_list"),
        tab: t("AddNodeDialog.NavTypes.tab"),
        nested_list: t("AddNodeDialog.NavTypes.nested_list"),
        list: t("AddNodeDialog.NavTypes.list"),
        content: t("AddNodeDialog.NavTypes.content"),
    };

    const TABLE_HEAD = [
        { id: "Id", label: t("NavigationStep.AdminTable.headers.Id"), alignRight: false, search: false },
        { id: "Title", label: t("NavigationStep.AdminTable.headers.Title"), alignRight: false, search: true },
        { id: "Type", label: t("NavigationStep.AdminTable.headers.Type"), alignRight: false, search: true },
        { id: "More", alignRight: true },
    ];
    
    const droppableTypes = ["content_list", "nested_list", "tab", "list"];
    const rootAllowedTypes = ["content_list", "nested_list", "tab", "list", "content"];
    const parentChildRules = {
        nested_list: ["tab", "content_list", "list", "content"],
        tab: ["content_list", "content"],
        content_list: ["content"],
        list: ["content_list", "content"],
    };

    const isSameId = (left, right) => {
        if (left == null || right == null) {
            return false;
        }
        return String(left) === String(right);
    };

    const isDescendant = (node, targetId) => {
        if (!node || !Array.isArray(node.children) || targetId == null) {
            return false;
        }
        for (const child of node.children) {
            if (isSameId(child.id, targetId) || isDescendant(child, targetId)) {
                return true;
            }
        }
        return false;
    };

    const normalizeParentId = (value) => {
        if (!value) return null;
        if (typeof value === "object" && "id" in value) return value.id;
        return value;
    };

    const normalizeSlug = (value) => {
        if (!value || typeof value !== "string") {
            return null;
        }
        return value.replace(/^\/+|\/+$/g, "");
    };

    const resolveFileUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) {
            return path;
        }
        const baseUrl = axios.defaults.baseURL || "";
        if (!baseUrl) return path;
        if (path.startsWith("/")) {
            return `${baseUrl}${path}`;
        }
        return `${baseUrl}/${path}`;
    };

    const getNodeKey = ({ node }) => node.id;

    useEffect(() => {
        return () => {
            if (scrollSearchResetTimerRef.current) {
                clearTimeout(scrollSearchResetTimerRef.current);
                scrollSearchResetTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        autoPlacementAppliedRef.current = false;
    }, [
        newNav?.newNode?.id,
        addContext?.view,
        addContext?.slug,
        addContext?.tabIndex,
        addContext?.activeSlug,
        addContext?.activeNavId,
        addContext?.targetParentId,
    ]);

    useEffect(() => {
        if (pendingScrollTargetIdRef.current == null || !Array.isArray(navAll)) {
            return;
        }
        const targetId = pendingScrollTargetIdRef.current;
        pendingScrollTargetIdRef.current = null;
        if (scrollSearchResetTimerRef.current) {
            clearTimeout(scrollSearchResetTimerRef.current);
        }
        setScrollSearchNodeId(targetId);
        scrollSearchResetTimerRef.current = setTimeout(() => {
            setScrollSearchNodeId(null);
            scrollSearchResetTimerRef.current = null;
        }, 500);
    }, [navAll]);

    useEffect(() => {
        if (navAll) {
            let tmpSelected = [];
            walk({
                treeData: navAll,
                getNodeKey: getNodeKey,
                callback: ({ node }) => {
                    if (currentNav) {
                        if (isSameId(node.contentId, currentNav.id) && node.parent) {
                            tmpSelected.push(
                                node.parent.id ? node.parent.id : node.parent
                            );
                        }
                    }
                },
                ignoreCollapsed: false,
            });
            setSelected(tmpSelected);
        }
    }, [navAll]);

    useEffect(() => {
        if (
            navElements &&
            Array.isArray(navElements) &&
            navElements.length > 0
        ) {
            setRows(
                navElements.map((row, index) => ({
                    Id: row.id,
                    Title: row.title,
                    Type: typeLabels[row.type] || row.type,
                    More: (
                        <MoreMenu
                            options={[
                                {
                                    label: t("buttonCommons.edit"),
                                    icon: <EditOutlinedIcon />,
                                    handler: () => {
                                        // editIssue(issues[index]);
                                    },
                                },
                                {
                                    label: t("buttonCommons.delete"),
                                    icon: <DeleteOutlineOutlinedIcon />,
                                    handler: () => {
                                        // deleteContent([
                                        //     issues[index].id,
                                        // ]);
                                    },
                                },
                            ]}
                        />
                    ),
                }))
            );
        }
    }, [navElements]);

    const { newNode, setNewNode } = newNav;
    const { removedNodes, setRemovedNodes } = removeNode;

    const nextTempId = () => {
        tempIdRef.current -= 1;
        return tempIdRef.current;
    };

    const findNodeMatchByContentSlug = (treeData, slug) => {
        const normalizedTarget = normalizeSlug(slug);
        if (!normalizedTarget || !Array.isArray(treeData) || treeData.length < 1) {
            return null;
        }
        const match = find({
            getNodeKey: getNodeKey,
            treeData,
            searchMethod: ({ node }) =>
                normalizeSlug(node.contentSlug) === normalizedTarget,
            expandAllMatchPaths: false,
            expandFocusMatchPaths: false,
        });
        return match.matches.length > 0 ? match.matches[0] : null;
    };

    const findNodeMatchById = (treeData, nodeId) => {
        if (nodeId == null || !Array.isArray(treeData) || treeData.length < 1) {
            return null;
        }
        const match = find({
            getNodeKey: getNodeKey,
            treeData,
            searchMethod: ({ node }) => isSameId(node.id, nodeId),
            expandAllMatchPaths: false,
            expandFocusMatchPaths: false,
        });
        return match.matches.length > 0 ? match.matches[0] : null;
    };

    const isAllowedUnderParent = (parentNode, childType) => {
        if (!childType) {
            return false;
        }
        if (!parentNode) {
            return rootAllowedTypes.includes(childType);
        }
        const allowedChildren = parentChildRules[parentNode.type] || [];
        return allowedChildren.includes(childType);
    };

    const expandPath = (treeData, path) => {
        if (!Array.isArray(path) || path.length < 1) {
            return treeData;
        }
        let expandedTree = treeData;
        for (let idx = 0; idx < path.length; idx += 1) {
            const partialPath = path.slice(0, idx + 1);
            const node = getNodeByIdPath(expandedTree, partialPath);
            if (!node || node.expanded) {
                continue;
            }
            expandedTree = changeNodeAtPath({
                treeData: expandedTree,
                path: partialPath,
                getNodeKey: getNodeKey,
                newNode: {
                    ...node,
                    expanded: true,
                },
            });
        }
        return expandedTree;
    };

    const findNearestAllowedAncestorMatch = (baseMatch, childType) => {
        if (!baseMatch || !Array.isArray(baseMatch.path) || baseMatch.path.length < 1) {
            return null;
        }
        for (let idx = baseMatch.path.length - 1; idx >= 0; idx -= 1) {
            const candidatePath = baseMatch.path.slice(0, idx + 1);
            const candidateNode = getNodeByIdPath(navAll, candidatePath);
            if (!candidateNode) {
                continue;
            }
            if (isAllowedUnderParent(candidateNode, childType)) {
                return {
                    node: candidateNode,
                    path: candidatePath,
                };
            }
        }
        return null;
    };

    const findFirstAllowedParentMatch = (treeData, childType) => {
        if (!Array.isArray(treeData) || treeData.length < 1) {
            return null;
        }
        const match = find({
            getNodeKey: getNodeKey,
            treeData,
            searchMethod: ({ node }) => isAllowedUnderParent(node, childType),
            expandAllMatchPaths: false,
            expandFocusMatchPaths: false,
        });
        return match.matches.length > 0 ? match.matches[0] : null;
    };

    const handleOnMoveNode = (treeData) => {
        treeData.node.parent = treeData.nextParentNode?.id || null;
        const parentChildren = treeData.nextParentNode
            ? treeData.nextParentNode.children
            : navAll;
        const updatedPosition = parentChildren.findIndex(
            (val) => isSameId(val.contentId, treeData.node.contentId)
        );
        if (updatedPosition !== -1) {
            setNavAll(
                changeNodeAtPath({
                    treeData: navAll,
                    path: treeData.path,
                    getNodeKey: getNodeKey,
                    newNode: {
                        ...treeData.node,
                        position: updatedPosition,
                    },
                })
            );
        }
        if (markNavChanged) {
            const affected = new Set();
            affected.add(treeData.node.id);
            if (treeData.prevParentNode && treeData.prevParentNode.children) {
                treeData.prevParentNode.children.forEach((n) =>
                    affected.add(n.id)
                );
            }
            if (treeData.nextParentNode && treeData.nextParentNode.children) {
                treeData.nextParentNode.children.forEach((n) =>
                    affected.add(n.id)
                );
            }
            // moving to root affects root siblings
            if (!treeData.nextParentNode && Array.isArray(navAll)) {
                navAll.forEach((n) => affected.add(n.id));
            }
            markNavChanged(Array.from(affected));
        }
    };

    const handleDelete = ({ node, path }) => {
        setNavAll(
            removeNodeAtPath({
                treeData: navAll,
                path: path,
                getNodeKey: getNodeKey,
            })
        );
        removeNode(node);
    };

    const openDeleteConfirm = (rowInfo) => {
        setDeleteRowInfo(rowInfo || null);
    };

    const closeDeleteConfirm = () => {
        setDeleteRowInfo(null);
    };

    const submitDeleteConfirm = () => {
        if (!deleteRowInfo) {
            return;
        }
        handleDelete(deleteRowInfo);
        setDeleteRowInfo(null);
    };

    const handleAddNode = ({
        nodeName,
        nodeType,
        position,
        icon,
        iconFileId,
        iconFile,
    }) => {
        const depth = currentRowInfo.path.length - 1 + position;
        let treeIndex = -1;
        if (
            !currentRowInfo.node.children ||
            currentRowInfo.node.children.length < 1
        ) {
            treeIndex = currentRowInfo.treeIndex;
        } else {
            if (position === 0) {
                treeIndex =
                    currentRowInfo.treeIndex +
                    currentRowInfo.node.children.length;
            } else {
                treeIndex = currentRowInfo.treeIndex + 1;
            }
        }
        setNavAll(
            insertNode({
                treeData: navAll,
                depth: depth,
                minimumTreeIndex: treeIndex,
                newNode: {
                    id: nextTempId(),
                    title: nodeName,
                    type: nodeType,
                    parent:
                        position === 1
                            ? currentRowInfo.node.id
                            : currentRowInfo.node.parent,
                    children: [],
                    icon: icon || "",
                    iconFileId: iconFileId || null,
                    iconFile: iconFile || null,
                },
                getNodeKey: getNodeKey,
                expandParent: true,
                // ignoreCollapsed: false,
            }).treeData
        );

        // reset current row info
        setCurrentRowInfo(null);
    };

    const handleEditNodeIcon = ({
        nodeName,
        icon,
        iconFileId,
        iconFile,
        nodeType,
    }) => {
        if (!editRowInfo) return;
        const nextType = nodeType || editRowInfo.node.type;
        const parentPath = editRowInfo.path.slice(0, -1);
        const parentNode =
            parentPath.length > 0 ? getNodeByIdPath(navAll, parentPath) : null;
        const parentType = parentNode ? parentNode.type : null;
        const allowedForParent =
            parentType === null
                ? rootAllowedTypes
                : parentChildRules[parentType] || [];
        if (!allowedForParent.includes(nextType)) {
            dispatch(
                showAlert({
                    message: t("NavigationStep.Errors.parentType"),
                    severity: "error",
                })
            );
            return;
        }
        const allowedChildren = parentChildRules[nextType] || [];
        if (Array.isArray(editRowInfo.node.children)) {
            const invalidChild = editRowInfo.node.children.find(
                (child) => !allowedChildren.includes(child.type)
            );
            if (invalidChild) {
                dispatch(
                    showAlert({
                        message: t("NavigationStep.Errors.childType", {
                            child: invalidChild.type,
                            parent: nextType,
                        }),
                        severity: "error",
                    })
                );
                return;
            }
        }
        const updatedTitle =
            typeof nodeName === "string" ? nodeName : editRowInfo.node.title;
        setNavAll(
            changeNodeAtPath({
                treeData: navAll,
                path: editRowInfo.path,
                getNodeKey: getNodeKey,
                newNode: {
                    ...editRowInfo.node,
                    title: updatedTitle,
                    type: nextType,
                    icon: icon || "",
                    iconFileId: iconFileId || null,
                    iconFile: iconFile || null,
                },
            })
        );
        if (markNavChanged) {
            markNavChanged([editRowInfo.node.id]);
        }
        setEditRowInfo(null);
    };

    const updateSiblingsPositions = (siblings) =>
        siblings.map((node, idx) => ({ ...node, position: idx }));

    const getNodeByIdPath = (treeData, path) => {
        if (!Array.isArray(path) || path.length === 0) {
            return null;
        }
        let current = { children: treeData };
        for (const id of path) {
            if (!current || !Array.isArray(current.children)) {
                return null;
            }
            const next = current.children.find((node) =>
                isSameId(node.id, id)
            );
            if (!next) {
                return null;
            }
            current = next;
        }
        return current;
    };

    useEffect(() => {
        const hasNavTree = Array.isArray(navAll) && navAll.length > 0;
        if (
            !newNode ||
            !hasNavTree ||
            !addContext ||
            !addContext.slug ||
            !addContext.view ||
            autoPlacementAppliedRef.current
        ) {
            return;
        }

        const existingNode = find({
            getNodeKey: getNodeKey,
            treeData: navAll,
            searchMethod: ({ node }) => isSameId(node.contentId, newNode.id),
            expandAllMatchPaths: false,
            expandFocusMatchPaths: false,
        });
        if (existingNode.matches.length > 0) {
            autoPlacementAppliedRef.current = true;
            return;
        }

        const sourceMatch =
            findNodeMatchByContentSlug(navAll, addContext.slug) ||
            findNodeMatchByContentSlug(navAll, addContext.activeSlug);
        if (!sourceMatch || !sourceMatch.node) {
            return;
        }

        let targetParentMatch = null;
        if (addContext.targetParentId != null) {
            const explicitParentMatch = findNodeMatchById(
                navAll,
                addContext.targetParentId
            );
            if (
                explicitParentMatch &&
                explicitParentMatch.node &&
                isAllowedUnderParent(explicitParentMatch.node, newNode.type)
            ) {
                targetParentMatch = explicitParentMatch;
            }
        }
        if (!targetParentMatch && addContext.view === "list") {
            targetParentMatch = findNearestAllowedAncestorMatch(
                sourceMatch,
                newNode.type
            );
        } else if (!targetParentMatch && addContext.view === "tabbed") {
            let activeMatch = null;
            if (addContext.activeNavId != null) {
                activeMatch = findNodeMatchById(navAll, addContext.activeNavId);
            }
            if (addContext.activeSlug) {
                activeMatch =
                    activeMatch ||
                    findNodeMatchByContentSlug(navAll, addContext.activeSlug);
            }
            if (!activeMatch) {
                const rawTabIndex = Number(addContext.tabIndex);
                if (Number.isInteger(rawTabIndex) && rawTabIndex > 0) {
                    const children = Array.isArray(sourceMatch.node.children)
                        ? [...sourceMatch.node.children].sort(
                            (a, b) => (a.position || 0) - (b.position || 0)
                        )
                        : [];
                    const activeNode = children[rawTabIndex - 1];
                    if (activeNode) {
                        activeMatch = findNodeMatchById(navAll, activeNode.id);
                    }
                }
            }

            if (activeMatch) {
                targetParentMatch = findNearestAllowedAncestorMatch(
                    activeMatch,
                    newNode.type
                );
            }
            if (!targetParentMatch) {
                targetParentMatch = findNearestAllowedAncestorMatch(
                    sourceMatch,
                    newNode.type
                );
            }
        } else if (!targetParentMatch && addContext.view === "detail") {
            const directParentId = normalizeParentId(sourceMatch.node.parent);
            if (directParentId != null) {
                const parentMatch = findNodeMatchById(navAll, directParentId);
                if (
                    parentMatch &&
                    parentMatch.node &&
                    isAllowedUnderParent(parentMatch.node, newNode.type)
                ) {
                    targetParentMatch = parentMatch;
                }
            }
            if (!targetParentMatch) {
                targetParentMatch = findNearestAllowedAncestorMatch(
                    sourceMatch,
                    newNode.type
                );
            }
        }

        if (!targetParentMatch) {
            targetParentMatch = findFirstAllowedParentMatch(navAll, newNode.type);
            if (
                process.env.NODE_ENV !== "production" &&
                typeof window !== "undefined" &&
                window.location.origin.includes("localhost")
            ) {
                console.warn(
                    "[NavigationStep] Fallback parent used for new node",
                    {
                        addContext,
                        sourceSlug: addContext.slug,
                        activeSlug: addContext.activeSlug,
                        activeNavId: addContext.activeNavId || null,
                        targetParentId: targetParentMatch?.node?.id || null,
                        targetParentType: targetParentMatch?.node?.type || null,
                    }
                );
            }
        }

        if (!targetParentMatch) {
            return;
        }

        const parentNode = targetParentMatch.node;
        const parentPath = targetParentMatch.path;
        const currentChildren = Array.isArray(parentNode.children)
            ? parentNode.children
            : [];
        const shiftedChildren = currentChildren.map((child, idx) => ({
            ...child,
            position: idx + 1,
        }));
        const newTempId = nextTempId();
        const insertedNode = {
            id: newTempId,
            contentId: newNode.id,
            contentSlug: newNode.contentSlug || null,
            title: newNode.title,
            originalTitle: newNode.title,
            type: newNode.type,
            parent: parentNode.id,
            originalParent: null,
            position: 0,
            originalPosition: 0,
            children: [],
        };

        let updatedTree = changeNodeAtPath({
            treeData: navAll,
            path: parentPath,
            getNodeKey: getNodeKey,
            newNode: {
                ...parentNode,
                expanded: true,
                children: [insertedNode, ...shiftedChildren],
            },
        });
        updatedTree = expandPath(updatedTree, parentPath);
        setNavAll(updatedTree);
        setNewNode({
            ...newNode,
            parent: parentNode.id,
        });
        pendingScrollTargetIdRef.current = newTempId;
        autoPlacementAppliedRef.current = true;

        if (markNavChanged) {
            markNavChanged([
                parentNode.id,
                newTempId,
                ...currentChildren.map((child) => child.id),
            ]);
        }
    }, [addContext, markNavChanged, navAll, newNode, setNavAll, setNewNode]);

    const getAllowedTypeOptions = (parentNode, currentType) => {
        const parentType = parentNode ? parentNode.type : null;
        const allowed =
            parentType === null
                ? rootAllowedTypes
                : parentChildRules[parentType] || [];
        const withCurrent =
            currentType && !allowed.includes(currentType)
                ? [...allowed, currentType]
                : allowed;
        return withCurrent
            .filter((type) => typeLabels[type])
            .map((type) => ({ value: type, label: typeLabels[type] }));
    };

    const getSiblingState = (rowInfo) => {
        const parentPath = rowInfo.path.slice(0, -1);
        const parentNode =
            parentPath.length > 0 ? getNodeByIdPath(navAll, parentPath) : null;
        const siblings = parentNode ? parentNode.children || [] : navAll;
        const index = siblings.findIndex((node) =>
            isSameId(node.id, rowInfo.node.id)
        );
        return { index, lastIndex: siblings.length - 1 };
    };

    const moveNodeByDirection = (rowInfo, direction) => {
        const parentPath = rowInfo.path.slice(0, -1);
        const parentNode =
            parentPath.length > 0 ? getNodeByIdPath(navAll, parentPath) : null;
        const siblings = parentNode ? parentNode.children || [] : navAll;
        if (!Array.isArray(siblings) || siblings.length < 2) {
            return;
        }
        const index = siblings.findIndex((node) =>
            isSameId(node.id, rowInfo.node.id)
        );
        const targetIndex = direction === "up" ? index - 1 : index + 1;
        if (index < 0 || targetIndex < 0 || targetIndex >= siblings.length) {
            return;
        }
        const newSiblings = [...siblings];
        const [moved] = newSiblings.splice(index, 1);
        newSiblings.splice(targetIndex, 0, moved);
        const updatedSiblings = updateSiblingsPositions(newSiblings);

        let newTree = navAll;
        if (parentNode) {
            newTree = changeNodeAtPath({
                treeData: navAll,
                path: parentPath,
                getNodeKey: getNodeKey,
                newNode: {
                    ...parentNode,
                    children: updatedSiblings,
                },
            });
        } else {
            newTree = updatedSiblings;
        }
        setNavAll(newTree);
        if (markNavChanged) {
            markNavChanged(updatedSiblings.map((node) => node.id));
        }
    };

    const handleSelectionChanged = (oldSelection, newSelection) => {
        const added = newSelection.filter((x) => !oldSelection.includes(x));
        const removed = oldSelection.filter((x) => !newSelection.includes(x));

        let newRemovedNodes = [...removedNodes];
        for (let i = 0; i < newRemovedNodes.length; ++i) {
            if (added.includes(newRemovedNodes[i])) {
                newRemovedNodes.splice(i, 1);
            }
        }
        newRemovedNodes = newRemovedNodes.concat(removed);
        setRemovedNodes(newRemovedNodes);

        if (currentNav) {
            const toAdd = find({
                getNodeKey: getNodeKey,
                treeData: navAll,
                searchMethod: (searchData) => {
                    const { node } = searchData;
                    if (added && added.length > 0) {
                        const addedIdx = added.findIndex(
                            (val) => isSameId(val, node.id)
                        );
                        if (addedIdx !== -1) {
                            added.splice(addedIdx, 1);
                            return true;
                        }
                        return false;
                    }
                },
                expandAllMatchPaths: false,
                expandFocusMatchPaths: false,
            });

            let newTree = [...navAll];
            for (const match of toAdd.matches) {
                const existingChildren = Array.isArray(match.node.children)
                    ? match.node.children
                    : [];
                const shiftedChildren = existingChildren.map((child, idx) => ({
                    ...child,
                    position: idx + 1,
                }));
                const createdNodeId = nextTempId();
                newTree = changeNodeAtPath({
                    treeData: newTree,
                    path: match.path,
                    getNodeKey: getNodeKey,
                    newNode: {
                        ...match.node,
                        children: [
                            {
                                id: createdNodeId,
                                contentId: currentNav.id,
                                title: currentNav.title,
                                type: currentNav.type,
                                parent: match.node.id,
                                position: 0,
                                children: [],
                            },
                            ...shiftedChildren,
                        ],
                    },
                    ignoreCollapsed: false,
                });
                if (markNavChanged) {
                    markNavChanged([
                        match.node.id,
                        createdNodeId,
                        ...existingChildren.map((child) => child.id),
                    ]);
                }
            }

            const toRemove = find({
                getNodeKey: getNodeKey,
                treeData: navAll,
                searchMethod: (searchData) => {
                    const { node } = searchData;
                    if (removed && removed.length > 0) {
                        const removedIdx = removed.findIndex(
                            (val) => isSameId(val, node.id)
                        );
                        if (removedIdx !== -1) {
                            removed.splice(removedIdx, 1);
                            return true;
                        }
                        return false;
                    }
                },
                expandAllMatchPaths: false,
                expandFocusMatchPaths: false,
            });

            for (const match of toRemove.matches) {
                const removeIdx = match.node.children.findIndex(
                    (val) => isSameId(val.contentId, currentNav.id)
                );
                if (removeIdx !== -1) {
                    let newChildren = [...match.node.children];
                    newChildren.splice(removeIdx, 1);
                    newTree = changeNodeAtPath({
                        treeData: newTree,
                        path: match.path,
                        getNodeKey: getNodeKey,
                        newNode: {
                            ...match.node,
                            children: [...newChildren],
                        },
                        ignoreCollapsed: false,
                    });
                }
            }

            setNavAll(newTree);
        }
    };

    const hasScrollSearch = scrollSearchNodeId != null;
    const hasCurrentNavSearch = Boolean(currentNav && Number(currentNav.id) > 0);
    const treeSearchEnabled = hasScrollSearch || hasCurrentNavSearch;
    const treeSearchMethod = ({ node, searchQuery }) => {
        if (typeof searchQuery === "number" || typeof searchQuery === "string") {
            return isSameId(node.id, searchQuery);
        }
        if (hasCurrentNavSearch) {
            return isSameId(node.contentId, currentNav.id);
        }
        return false;
    };

    return (
        <TabbedPage orientation="horizontal" dialog={true}>
            <div style={{ height: 400, width: "100%" }} label={t("NavigationStep.Draggable.label")}>
                <DndProvider backend={HTML5Backend}>
                    {selected.length === 0 && newNode && (
                        <Box
                            sx={{
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                                backgroundColor: "background.paper",
                                pb: 1,
                                mb: 1,
                            }}
                        >
                            <ExternalNode
                                onNodeDropped={() => {
                                    // setIsNewNodeDropped(true);
                                }}
                                node={{
                                    id: nextTempId(),
                                    title: newNode.title,
                                    contentId: newNode.id,
                                    parent: null,
                                    originalParent: null,
                                    originalTitle: newNode.title,
                                    type: newNode.type,
                                    position: 0,
                                    originalPosition: 0,
                                    children: [],
                                }}
                                displayTitle={
                                    newNode.title ||
                                    t("NavigationStep.ExternalNode.placeholder")
                                }
                            />
                        </Box>
                    )}
                    <SortableTree
                        treeData={navAll}
                        onChange={(newTree) => {
                            setNavAll(newTree);
                        }}
                        searchQuery={hasScrollSearch ? scrollSearchNodeId : undefined}
                        searchFocusOffset={hasScrollSearch ? 0 : undefined}
                        searchMethod={treeSearchEnabled ? treeSearchMethod : undefined}
                        onlyExpandSearchedNodes={!hasScrollSearch}
                        getNodeKey={getNodeKey}
                        onMoveNode={handleOnMoveNode}
                        dndType={externalNodeType}
                        generateNodeProps={(rowInfo) => {
                            const siblingState = getSiblingState(rowInfo);
                            return {
                                title: (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                    >
                                        {(() => {
                                            const iconFilePathRaw =
                                                rowInfo.node.iconFile &&
                                                (rowInfo.node.iconFile.filePath ||
                                                    rowInfo.node.iconFile.file_path);
                                            const iconFilePath = resolveFileUrl(
                                                iconFilePathRaw
                                            );
                                            if (iconFilePath) {
                                                return (
                                                    <Box
                                                        component="img"
                                                        src={iconFilePath}
                                                        alt={rowInfo.node.title}
                                                        sx={{
                                                            width: 18,
                                                            height: 18,
                                                            objectFit: "contain",
                                                        }}
                                                    />
                                                );
                                            }
                                            if (rowInfo.node.icon) {
                                                return (
                                                    <span className="material-icons">
                                                        {rowInfo.node.icon}
                                                    </span>
                                                );
                                            }
                                            return null;
                                        })()}
                                        <TextField
                                            id="outlined-name"
                                            value={rowInfo.node.title}
                                            onChange={(event) => {
                                                const title = event.target.value;
                                                if (rowInfo.node.id === -1) {
                                                    setNewNode({
                                                        ...rowInfo.node,
                                                        title,
                                                    });
                                                }
                                                setNavAll(
                                                    changeNodeAtPath({
                                                        treeData: navAll,
                                                        path: rowInfo.path,
                                                        getNodeKey: getNodeKey,
                                                        newNode: {
                                                            ...rowInfo.node,
                                                            title,
                                                        },
                                                    })
                                                );
                                                if (markNavChanged) {
                                                    markNavChanged([rowInfo.node.id]);
                                                }
                                            }}
                                        />
                                    </Stack>
                                ),
                                buttons: [
                                    droppableTypes.includes(rowInfo.node.type) && (
                                        <Tooltip title={t("NavigationStep.Draggable.add.title")}>
                                            <IconButton
                                        color="primary"
                                                aria-label={t("NavigationStep.Draggable.add.label")}
                                                component="span"
                                                onClick={() => {
                                                    setCurrentRowInfo(rowInfo);
                                                    setShowAddNodeDialog(true);
                                                }}
                                            >
                                                <AddIcon />
                                            </IconButton>
                                        </Tooltip>
                                    ),
                                    <Tooltip title={t("buttonCommons.edit")}>
                                        <IconButton
                                            color="primary"
                                            aria-label={t("buttonCommons.edit")}
                                            component="span"
                                            onClick={() => {
                                                setEditRowInfo(rowInfo);
                                                setShowEditIconDialog(true);
                                            }}
                                        >
                                            <EditOutlinedIcon />
                                        </IconButton>
                                    </Tooltip>,
                                    <Tooltip title={t("NavigationStep.Draggable.moveUp")}>
                                        <span>
                                            <IconButton
                                                color="primary"
                                                aria-label={t("NavigationStep.Draggable.moveUp")}
                                                component="span"
                                                disabled={siblingState.index <= 0}
                                                onClick={() =>
                                                    moveNodeByDirection(rowInfo, "up")
                                                }
                                            >
                                                <ArrowUpwardIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>,
                            <Tooltip title={t("NavigationStep.Draggable.moveDown")}>
                                <span>
                                    <IconButton
                                                color="primary"
                                                aria-label={t("NavigationStep.Draggable.moveDown")}
                                                component="span"
                                                disabled={
                                                    siblingState.index >=
                                                    siblingState.lastIndex
                                                }
                                                onClick={() =>
                                                    moveNodeByDirection(rowInfo, "down")
                                                }
                                            >
                                                <ArrowDownwardIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>,
                                    <Tooltip title={t("NavigationStep.Draggable.delete.title")}>
                                        <IconButton
                                            color="primary"
                                            aria-label={t("NavigationStep.Draggable.delete.label")}
                                            component="span"
                                            onClick={() => openDeleteConfirm(rowInfo)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>,
                                ],
                            };
                        }}
                        canDrop={({ node, nextParent }) => {
                            const currentParentId = normalizeParentId(node.parent);
                            if (nextParent) {
                                if (isSameId(currentParentId, nextParent.id)) {
                                    return true;
                                }
                                if (isSameId(nextParent.id, node.id)) {
                                    dispatch(
                                        showAlert({
                                            message: t("NavigationStep.Errors.selfDrop"),
                                            severity: "error",
                                        })
                                    );
                                    return false;
                                }
                                if (isDescendant(node, nextParent.id)) {
                                    dispatch(
                                        showAlert({
                                            message: t("NavigationStep.Errors.cycle"),
                                            severity: "error",
                                        })
                                    );
                                    return false;
                                }
                                if (!droppableTypes.includes(nextParent.type)) {
                                    dispatch(
                                        showAlert({
                                            message: t("NavigationStep.Errors.parentType"),
                                            severity: "error",
                                        })
                                    );
                                    return false;
                                }
                                const allowedChildren =
                                    parentChildRules[nextParent.type] || [];
                                if (!allowedChildren.includes(node.type)) {
                                    dispatch(
                                        showAlert({
                                            message: t("NavigationStep.Errors.childType", {
                                                child: node.type,
                                                parent: nextParent.type,
                                            }),
                                            severity: "error",
                                        })
                                    );
                                    return false;
                                }
                            } else {
                                if (!rootAllowedTypes.includes(node.type)) {
                                    dispatch(
                                        showAlert({
                                            message: t("NavigationStep.Errors.rootType", {
                                                type: node.type,
                                            }),
                                            severity: "error",
                                        })
                                    );
                                    return false;
                                }
                            }
                            return true;
                        }}
                    />
                </DndProvider>
                <AddNodeDialog
                    title={t("NavigationStep.AddNodeDialog.title")}
                    contentText={t("NavigationStep.AddNodeDialog.contentText")}
                    textLabel={t("NavigationStep.AddNodeDialog.textLabel")}
                    onSubmit={handleAddNode}
                    open={showAddNodeDialog}
                    onClose={() => {
                        setShowAddNodeDialog(false);
                    }}
                    validationList={[]}
                    errorText={{
                        empty: t("NavigationStep.AddNodeDialog.errorText.empty"),
                        alreadyExists: t("NavigationStep.AddNodeDialog.errorText.alreadyExists"),
                    }}
                    parentNodeTitle={
                        currentRowInfo &&
                        currentRowInfo.node &&
                        currentRowInfo.node.title
                            ? currentRowInfo.node.title
                            : ""
                    }
                />
                <AddNodeDialog
                    title={t("buttonCommons.edit")}
                    contentText={t("NavigationStep.AddNodeDialog.contentText")}
                    textLabel={t("NavigationStep.AddNodeDialog.textLabel")}
                    onSubmit={handleEditNodeIcon}
                    open={showEditIconDialog}
                    onClose={() => {
                        setShowEditIconDialog(false);
                        setEditRowInfo(null);
                    }}
                    validationList={[]}
                    errorText={{
                        empty: t("NavigationStep.AddNodeDialog.errorText.empty"),
                        alreadyExists: t("NavigationStep.AddNodeDialog.errorText.alreadyExists"),
                    }}
                    parentNodeTitle={
                        editRowInfo && editRowInfo.node && editRowInfo.node.title
                            ? editRowInfo.node.title
                            : ""
                    }
                    initialTitle={
                        editRowInfo && editRowInfo.node
                            ? editRowInfo.node.title
                            : ""
                    }
                    initialNodeType={
                        editRowInfo && editRowInfo.node
                            ? editRowInfo.node.type
                            : ""
                    }
                    initialIcon={
                        editRowInfo && editRowInfo.node
                            ? editRowInfo.node.icon
                            : ""
                    }
                    initialIconFile={
                        editRowInfo && editRowInfo.node
                            ? editRowInfo.node.iconFile
                            : null
                    }
                    typeOptions={
                        editRowInfo
                            ? getAllowedTypeOptions(
                                  editRowInfo.path.length > 1
                                      ? getNodeByIdPath(
                                            navAll,
                                            editRowInfo.path.slice(0, -1)
                                        )
                                      : null,
                                  editRowInfo.node.type
                              )
                            : null
                    }
                    hidePlacement
                />
                <ConfirmDialog
                    open={Boolean(deleteRowInfo)}
                    onClose={closeDeleteConfirm}
                    onSubmit={submitDeleteConfirm}
                    title={t("ConfirmDialog.deleteTitle", {
                        defaultValue: "Confirm deletion",
                    })}
                    text={t("ConfirmDialog.deleteMenuNodeText", {
                        defaultValue:
                            "Do you really want to delete this menu element? This action cannot be undone.",
                    })}
                    confirmLabel={t("buttonCommons.delete")}
                    confirmColor="error"
                />
            </div>
            <AdminTable
                label={t("NavigationStep.AdminTable.label")}
                searchPlaceholder={t("AdminTableCommons.searchPlaceholder")}
                headers={TABLE_HEAD}
                rows={rows}
                selection={{
                    selected: selected,
                    setSelected: setSelected,
                }}
                onSelectionChanged={handleSelectionChanged}
                showChildrenWhenSelected={true}
                contextMenuOptions={[
                    {
                        label: t("buttonCommons.edit"),
                        icon: <EditOutlinedIcon />,
                        handler: (id) => {
                            // editIssue(issues[id]);
                        },
                    },
                    {
                        label: t("buttonCommons.delete"),
                        icon: <DeleteOutlineOutlinedIcon />,
                        handler: (id) => {
                            // deleteContent([issues[id].id]);
                        },
                    },
                ]}
            >
                <Tooltip title={t("buttonCommons.delete")}>
                    <IconButton
                        color="primary"
                        aria-label={t("AdminTableCommons.deleteSelected")}
                        component="span"
                        // onClick={handleDeleteSelected}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </AdminTable>
        </TabbedPage>
    );
};

export default NavigationStep;
